import { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { SHORTCUT_MAP, type ShortcutGroup } from '@/lib/shortcuts'

function Kbd({ children }: { children: string }) {
  return (
    <kbd className="inline-flex items-center gap-0.5 rounded border bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
      {children}
    </kbd>
  )
}

function ShortcutGroupCard({ group }: { group: ShortcutGroup }) {
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-foreground">{group.name}</h3>
      <div className="space-y-1.5">
        {group.shortcuts.map((s) => (
          <div key={s.keys + s.description} className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{s.description}</span>
            <Kbd>{s.keys}</Kbd>
          </div>
        ))}
      </div>
    </div>
  )
}

export function KeyboardShortcutsOverlay() {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === '?') {
        e.preventDefault()
        setOpen((prev) => !prev)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const handleClose = useCallback((isOpen: boolean) => {
    if (!isOpen) setOpen(false)
  }, [])

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-lg"
        data-testid="shortcuts-overlay"
      >
        <DialogHeader>
          <DialogTitle>Keyboard Shortcuts</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          {SHORTCUT_MAP.map((group) => (
            <ShortcutGroupCard key={group.name} group={group} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}
